import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackFeatureUsage, trackEvent, trackFormSubmission } from '../utils/analytics';

const PendingRequests = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [clubChangeRequests, setClubChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Get initial tab from URL parameters
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (['promotion', 'clubchange', 'conversion'].includes(tabParam)) {
      return tabParam;
    }
    return 'promotion';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  useEffect(() => {
    fetchPendingRequests();
    fetchPendingClubChangeRequests();
    // Track pending requests page view
    trackFeatureUsage('Pending Requests', 'view');
  }, []);

  // Update active tab when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (['promotion', 'clubchange', 'conversion'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get('/api/promotion-requests/pending');
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
      setError('Failed to load pending requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingClubChangeRequests = async () => {
    try {
      const response = await axios.get('/api/club-change-requests/pending');
      setClubChangeRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch pending club change requests:', error);
      setError('Failed to load pending club change requests. Please try again.');
    }
  };

  const handleActionRequest = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComments('');
    setActionError('');
    setShowActionModal(true);
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();
    if (!comments.trim()) {
      setActionError('Please provide comments for your decision');
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      if (!selectedRequest || !selectedRequest.id) {
        setActionError('Invalid request selected. Please try again.');
        return;
      }

      let endpoint;
      if (activeTab === 'promotion' || activeTab === 'conversion') {
        endpoint = actionType === 'approve' 
          ? `/api/promotion-requests/${selectedRequest.id}/approve`
          : `/api/promotion-requests/${selectedRequest.id}/reject`;
      } else {
        endpoint = actionType === 'approve' 
          ? `/api/club-change-requests/${selectedRequest.id}/approve`
          : `/api/club-change-requests/${selectedRequest.id}/reject`;
      }

      await axios.post(endpoint, {
        comments: comments.trim()
      });

      // Track promotion request action
      trackEvent('promotion_request_action', {
        event_category: 'Promotion Management',
        event_label: actionType,
        customer_id: selectedRequest.customer?.id,
        requesting_coach_id: selectedRequest.requestedBy?.id,
        action_type: actionType
      });
      
      trackFormSubmission(`${actionType}_promotion_request_form`, true);

      setShowActionModal(false);
      if (activeTab === 'promotion' || activeTab === 'conversion') {
        if (activeTab === 'conversion' || selectedRequest.requestedSystemRole === 'SYSTEM_CUSTOMER') {
          setConfirmMessage(
            actionType === 'approve' 
              ? `Conversion request approved successfully! ${selectedRequest.requestedBy?.fullName || 'User'} has been converted to customer.`
              : `Conversion request rejected. The requesting coach has been notified.`
          );
        } else {
          setConfirmMessage(
            actionType === 'approve' 
              ? `Promotion request approved successfully! ${selectedRequest.customer?.fullName || 'Customer'} has been promoted to coach.`
              : `Promotion request rejected. The requesting coach has been notified.`
          );
        }
        fetchPendingRequests();
      } else {
        setConfirmMessage(
          actionType === 'approve' 
            ? `Club change request approved successfully! ${selectedRequest.customer?.fullName || 'Customer'} has been moved to ${selectedRequest.newCoach?.fullName || 'new coach'}.`
            : `Club change request rejected. The customer has been notified.`
        );
        fetchPendingClubChangeRequests();
      }
      setShowConfirmModal(true);
    } catch (error) {
      console.error(`Failed to ${actionType} request:`, error);
      setActionError(error.response?.data?.message || `Failed to ${actionType} request. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'var(--warning)';
      case 'APPROVED': return 'var(--success)';
      case 'REJECTED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const getRequestTypeIcon = (currentRole, requestedRole) => {
    if (currentRole === 'SYSTEM_CUSTOMER' && requestedRole === 'SYSTEM_EDITOR') {
      return '🎓';
    }
    return '📈';
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading pending requests...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Pending Promotion Requests</h1>
      
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #ED8936 0%, #C05621 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          🎯 System Approver Access
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          Review and approve customer promotion requests from coaches
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Tab Navigation */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid var(--gray-medium)', 
          marginBottom: '0' 
        }}>
          <button
            onClick={() => setActiveTab('promotion')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'promotion' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'promotion' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            Promotion Requests ({requests.filter(r => r.requestedSystemRole === 'SYSTEM_EDITOR').length})
          </button>
          <button
            onClick={() => setActiveTab('clubchange')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'clubchange' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'clubchange' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            Club Change Requests ({clubChangeRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('conversion')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'conversion' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'conversion' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500'
            }}
          >
            Transition to Customer ({requests.filter(r => r.requestedSystemRole === 'SYSTEM_CUSTOMER').length})
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            {activeTab === 'promotion' 
              ? `Promotion Requests (${requests.filter(r => r.requestedSystemRole === 'SYSTEM_EDITOR').length} pending)`
              : activeTab === 'clubchange'
              ? `Club Change Requests (${clubChangeRequests.length} pending)`
              : `Transition to Customer Requests (${requests.filter(r => r.requestedSystemRole === 'SYSTEM_CUSTOMER').length} pending)`
            }
          </h3>
          <button 
            className="btn btn-secondary"
            onClick={() => {
              if (activeTab === 'promotion') {
                fetchPendingRequests();
              } else {
                fetchPendingClubChangeRequests();
              }
            }}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>

        {activeTab === 'promotion' ? (
          requests.filter(r => r.requestedSystemRole === 'SYSTEM_EDITOR').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No pending promotion requests found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {requests.filter(r => r.requestedSystemRole === 'SYSTEM_EDITOR').map((request) => (
              <div
                key={request.id}
                style={{
                  border: '2px solid var(--light-blue)',
                  borderRadius: '12px',
                  padding: '20px',
                  background: 'var(--white)',
                  boxShadow: '0 2px 8px rgba(74, 144, 226, 0.1)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '24px' }}>
                      {getRequestTypeIcon(request.currentSystemRole, request.requestedSystemRole)}
                    </span>
                    <div>
                      <h4 style={{ color: 'var(--dark-blue)', margin: 0, marginBottom: '5px' }}>
                        Customer → Coach Promotion Request
                      </h4>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        Request ID: #{request.id} • Created: {new Date(request.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <span style={{
                    background: getStatusColor(request.status),
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {request.status}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                  <div>
                    <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Customer Details</h5>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Name:</strong> {request.customer.fullName}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Email:</strong> {request.customer.email}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Current Status:</strong> {request.customer.status}
                    </div>
                  </div>

                  <div>
                    <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Requesting Coach</h5>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Name:</strong> {request.requestedBy.fullName}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Email:</strong> {request.requestedBy.email}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Status:</strong> {request.requestedBy.status}
                    </div>
                  </div>
                </div>

                <div style={{
                  background: 'var(--pale-blue)',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '15px'
                }}>
                  <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>Promotion Details</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
                    <div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Member Role:</strong> {request.currentMemberRole} → {request.requestedMemberRole}
                      </div>
                      <div>
                        <strong>System Role:</strong> {request.currentSystemRole} → {request.requestedSystemRole}
                      </div>
                    </div>
                    <div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>New Permissions:</strong> Coach Access
                      </div>
                      <div>
                        <strong>Impact:</strong> All referrals move to requesting coach
                      </div>
                    </div>
                  </div>
                </div>

                {request.requestReason && (
                  <div style={{
                    background: 'var(--gray-light)',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '15px'
                  }}>
                    <strong>Reason:</strong> {request.requestReason}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleActionRequest(request, 'reject')}
                    style={{ 
                      background: 'var(--error)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    ❌ Reject
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleActionRequest(request, 'approve')}
                    style={{ 
                      background: 'var(--success)',
                      border: 'none'
                    }}
                  >
                    ✅ Approve
                  </button>
                </div>
              </div>
              ))}
            </div>
          )
        ) : activeTab === 'clubchange' ? (
          // Club Change Requests Tab
          clubChangeRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No pending club change requests found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {clubChangeRequests.map((request) => (
                <div
                  key={request.id}
                  style={{
                    border: '2px solid var(--light-blue)',
                    borderRadius: '12px',
                    padding: '20px',
                    background: 'var(--white)',
                    boxShadow: '0 2px 8px rgba(74, 144, 226, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>🏢</span>
                      <div>
                        <h4 style={{ color: 'var(--dark-blue)', margin: 0, marginBottom: '5px' }}>
                          Club Change Request
                        </h4>
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                          Request ID: #{request.id} • Created: {new Date(request.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      background: getStatusColor(request.status),
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {request.status}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                    <div>
                      <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Customer</h5>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Name:</strong> {request.customer.fullName}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Email:</strong> {request.customer.email}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Status:</strong> {request.customer.status}
                      </div>
                    </div>

                    <div>
                      <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Current Coach</h5>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Name:</strong> {request.currentCoach.fullName}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Email:</strong> {request.currentCoach.email}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Status:</strong> {request.currentCoach.status}
                      </div>
                    </div>

                    <div>
                      <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Requested Coach</h5>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Name:</strong> {request.newCoach.fullName}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Email:</strong> {request.newCoach.email}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Status:</strong> {request.newCoach.status}
                      </div>
                    </div>
                  </div>

                  {request.reason && (
                    <div style={{
                      background: 'var(--gray-light)',
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '15px'
                    }}>
                      <strong>Reason:</strong> {request.reason}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleActionRequest(request, 'reject')}
                      style={{ 
                        background: 'var(--error)',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      ❌ Reject
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleActionRequest(request, 'approve')}
                      style={{ 
                        background: 'var(--success)',
                        border: 'none'
                      }}
                    >
                      ✅ Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // Transition to Customer Requests Tab
          requests.filter(r => r.requestedSystemRole === 'SYSTEM_CUSTOMER').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No pending transition to customer requests found.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {requests.filter(r => r.requestedSystemRole === 'SYSTEM_CUSTOMER').map((request) => (
                <div
                  key={request.id}
                  style={{
                    border: '2px solid var(--light-blue)',
                    borderRadius: '12px',
                    padding: '20px',
                    background: 'var(--white)',
                    boxShadow: '0 2px 8px rgba(74, 144, 226, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>🔄</span>
                      <div>
                        <h4 style={{ color: 'var(--dark-blue)', margin: 0, marginBottom: '5px' }}>
                          Coach → Customer Conversion Request
                        </h4>
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                          Request ID: #{request.id} • Created: {new Date(request.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      background: getStatusColor(request.status),
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {request.status}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                    <div>
                      <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Coach Requesting Conversion</h5>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Name:</strong> {request.requestedBy.fullName}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Email:</strong> {request.requestedBy.email}
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Current Status:</strong> {request.requestedBy.status}
                      </div>
                    </div>

                    <div>
                      <h5 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Conversion Details</h5>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Current Role:</strong> {request.currentMemberRole} (Coach)
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Requested Role:</strong> {request.requestedMemberRole} (Customer)
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>System Access:</strong> {request.currentSystemRole} → {request.requestedSystemRole}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    background: 'var(--pale-blue)',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>Impact of Approval</h5>
                    <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Role Change:</strong> Coach → Customer
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>System Access:</strong> SYSTEM_EDITOR → SYSTEM_CUSTOMER
                      </div>
                      <div>
                        <strong>Customer Movement:</strong> All immediate customers will move to parent coach
                      </div>
                    </div>
                  </div>

                  {request.requestReason && (
                    <div style={{
                      background: 'var(--gray-light)',
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '15px'
                    }}>
                      <strong>Reason:</strong> {request.requestReason}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleActionRequest(request, 'reject')}
                      style={{ 
                        background: 'var(--error)',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      ❌ Reject
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleActionRequest(request, 'approve')}
                      style={{ 
                        background: 'var(--success)',
                        border: 'none'
                      }}
                    >
                      ✅ Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                {actionType === 'approve' ? '✅ Approve' : '❌ Reject'} Promotion Request
              </h3>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedRequest(null);
                  setActionError('');
                  setComments('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            {actionError && (
              <div className="alert alert-error">{actionError}</div>
            )}

            {/* Request Summary */}
            <div style={{
              background: actionType === 'approve' ? 'var(--pale-blue)' : '#FED7D7',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: `2px solid ${actionType === 'approve' ? 'var(--primary-blue)' : 'var(--error)'}`
            }}>
              <h4 style={{ 
                color: actionType === 'approve' ? 'var(--dark-blue)' : '#C53030', 
                marginBottom: '10px' 
              }}>
                Request Summary
              </h4>
              <div style={{ fontSize: '14px' }}>
                {activeTab === 'conversion' ? (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Coach Requesting Conversion:</strong> {selectedRequest.requestedBy.fullName} ({selectedRequest.requestedBy.email})
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Conversion:</strong> {selectedRequest.currentMemberRole} → {selectedRequest.requestedMemberRole}
                    </div>
                    <div>
                      <strong>System Access:</strong> {selectedRequest.currentSystemRole} → {selectedRequest.requestedSystemRole}
                    </div>
                  </>
                ) : activeTab === 'promotion' ? (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Customer:</strong> {selectedRequest.customer.fullName} ({selectedRequest.customer.email})
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Requesting Coach:</strong> {selectedRequest.requestedBy.fullName} ({selectedRequest.requestedBy.email})
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Promotion:</strong> {selectedRequest.currentMemberRole} → {selectedRequest.requestedMemberRole}
                    </div>
                    <div>
                      <strong>System Access:</strong> {selectedRequest.currentSystemRole} → {selectedRequest.requestedSystemRole}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Customer:</strong> {selectedRequest.customer.fullName} ({selectedRequest.customer.email})
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Current Coach:</strong> {selectedRequest.currentCoach.fullName} ({selectedRequest.currentCoach.email})
                    </div>
                    <div>
                      <strong>Requested Coach:</strong> {selectedRequest.newCoach.fullName} ({selectedRequest.newCoach.email})
                    </div>
                  </>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmitAction}>
              <div className="form-group">
                <label htmlFor="comments" className="form-label">
                  {actionType === 'approve' ? 'Approval' : 'Rejection'} Comments *
                </label>
                <textarea
                  id="comments"
                  className="form-input"
                  rows="4"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  required
                  disabled={actionLoading}
                  placeholder={
                    actionType === 'approve' 
                      ? `Explain why you are approving this ${activeTab === 'promotion' ? 'promotion' : activeTab === 'clubchange' ? 'club change' : 'conversion'} request...`
                      : `Explain why you are rejecting this ${activeTab === 'promotion' ? 'promotion' : activeTab === 'clubchange' ? 'club change' : 'conversion'} request...`
                  }
                />
              </div>

              {actionType === 'approve' && (
                <div style={{
                  background: '#FFF3CD',
                  border: '1px solid #FFEAA7',
                  padding: '15px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  <strong>⚠️ Important:</strong> Approving this request will:
                  <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                    {activeTab === 'promotion' ? (
                      selectedRequest.requestedSystemRole === 'SYSTEM_CUSTOMER' ? (
                        <>
                          <li>Change {selectedRequest.requestedBy.fullName}'s role from Coach to Customer</li>
                          <li>Remove their SYSTEM_EDITOR permissions</li>
                          <li>Move ALL their immediate customers to their parent coach</li>
                          <li>This action can be reversed with another promotion request</li>
                        </>
                      ) : (
                        <>
                          <li>Change {selectedRequest.customer.fullName}'s role from Customer to Associate</li>
                          <li>Grant them SYSTEM_EDITOR permissions (coach access)</li>
                          <li>Move ALL their referrals to become direct customers of {selectedRequest.requestedBy.fullName}</li>
                          <li>This action cannot be easily undone</li>
                        </>
                      )
                    ) : activeTab === 'clubchange' ? (
                      <>
                        <li>Move {selectedRequest.customer.fullName} from {selectedRequest.currentCoach.fullName} to {selectedRequest.newCoach.fullName}</li>
                        <li>Update the customer's hierarchy relationship</li>
                        <li>The customer will now be under the new coach's management</li>
                        <li>This action can be reversed with another club change request</li>
                      </>
                    ) : (
                      <>
                        <li>Change {selectedRequest.requestedBy.fullName}'s role from Coach to Customer</li>
                        <li>Remove their SYSTEM_EDITOR permissions</li>
                        <li>Move ALL their immediate customers to their parent coach</li>
                        <li>This action can be reversed with another promotion request</li>
                      </>
                    )}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedRequest(null);
                    setActionError('');
                    setComments('');
                  }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={actionLoading || !comments.trim()}
                  style={{
                    background: actionType === 'approve' ? 'var(--success)' : 'var(--error)',
                    border: 'none'
                  }}
                >
                  {actionLoading 
                    ? (actionType === 'approve' ? 'Approving...' : 'Rejecting...') 
                    : (actionType === 'approve' ? 'Approve Request' : 'Reject Request')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>
              {actionType === 'approve' ? '✅' : '❌'}
            </div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              {actionType === 'approve' ? 'Request Approved!' : 'Request Rejected'}
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRequests;
